import { z } from 'zod/v4';

export const GetImpactAreasHierarchyParamsSchema = z.object({
  sectionId: z.string().min(1),
});

export type GetImpactAreasHierarchyParams = z.infer<
  typeof GetImpactAreasHierarchyParamsSchema
>;

export const ImpactAreaSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  sectionId: z.string().min(1),
  parentId: z.string().nullable(),
  isParent: z.number().nullable(),
  level: z.number().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type ImpactArea = z.infer<typeof ImpactAreaSchema>;

export const ImpactAreasHierarchyResponseSchema = z.array(ImpactAreaSchema);

export type ImpactAreasHierarchyResponse = z.infer<
  typeof ImpactAreasHierarchyResponseSchema
>;

/**
 * One row of the flat impact-area list (`GET /api/impact-areas`).
 *
 * Reference data, shared by every tenant: the whole of
 * `companies_service_impact_areas`, ordered by `sectionId`. This is the list
 * behind every CAPA impact-area dropdown and facet, replacing the legacy
 * `GET /api-service/v3/impact-areas` read of the retired `impact_areas` table.
 *
 * `sectionId` is the dotted path (`'2.1.3'`) that orders and nests the list:
 * consumers derive depth from the number of segments and children from the
 * `startsWith(parent + '.')` prefix. It is the same numbering the legacy table
 * called `hierarchyId`.
 *
 * `id` is the stable modern identifier (`WATER_MANAGEMENT`) and the only value
 * that may be written into `impactAreaId` columns. The legacy random id lives
 * on the table as `depreciated_id` and is deliberately not on this contract —
 * it exists only for the one-off data remap and is dropped with the table.
 */
export const ImpactAreaListItemSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  sectionId: z.string().min(1),
});

export type ImpactAreaListItem = z.infer<typeof ImpactAreaListItemSchema>;

export const ListImpactAreasResponseSchema = z.array(ImpactAreaListItemSchema);

export type ListImpactAreasResponse = z.infer<
  typeof ListImpactAreasResponseSchema
>;

export const ImpactAreaTreeItemSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  sectionId: z.string().min(1),
  childCount: z.number().int(),
});

export type ImpactAreaTreeItem = z.infer<typeof ImpactAreaTreeItemSchema>;

export const ListImpactAreaTreeQuerySchema = z.object({
  parentSectionId: z.string().min(1).optional(),
});

export type ListImpactAreaTreeQuery = z.infer<
  typeof ListImpactAreaTreeQuerySchema
>;

export const GetImpactAreaByIdParamsSchema = z.object({
  id: z.string().min(1),
});

export type GetImpactAreaByIdParams = z.infer<
  typeof GetImpactAreaByIdParamsSchema
>;
